import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import uuid from 'react-uuid'
import { Dialog, DialogType, IconButton, Stack } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import { SquareRegular } from '@fluentui/react-icons'
import { ArrowDownward, Check, Close, ExpandMore, HelpOutlineOutlined } from '@mui/icons-material'
import { Alert, IconButton as MuiIconButton, Input, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import DOMPurify from 'dompurify'
import { filter, first, isEmpty, trim, words } from 'lodash'
import rehypeRaw from 'rehype-raw'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'

import {
  AzureSqlServerExecResults,
  ChatErrorResponse,
  ChatHistoryLoadingState,
  ChatMessage,
  Citation,
  Conversation,
  CosmosDBStatus,
  createExperimentForm,
  ErrorMessage,
  expandCollapseExperimentForm,
  historyDelete,
  historyRename,
  ToolMessageContent,
} from '../../api'
import AssistantIcon from '../../assets/assistant_icon.svg'
import CollapseButton from '../../assets/collapse_button.svg'
import DeleteIcon from '../../assets/delete.svg'
import InfoBalloon from '../../assets/info_balloon.svg'
import PenIcon from '../../assets/pen.svg'
import SparkleGradient from '../../assets/sparkle_gradient.svg'
import { Answer } from '../../components/Answer'
import { CardsCarousel } from '../../components/CardsCarousel'
import { CustomTooltip } from '../../components/CustomTooltip'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { ExperimentForm } from '../../components/ExperimentForm'
import { JumpingDots } from '../../components/JumpingDots'
import { QuestionInput } from '../../components/QuestionInput'
import { ThinkingAnimation } from '../../components/ThinkingAnimation'
import { AgentNames } from '../../constants/agentNames'
import { getCreateExperimentBlockedMessage } from '../../constants/createExperimentBlockedMessages'
import { XSSAllowTags } from '../../constants/sanatizeAllowables'
import { useWebSocket } from '../../hooks/useWebSocket'
import { AppStateContext } from '../../state/AppProvider'
import { getCreateExperimentBlock } from '../../utils/permissions'
import { getRandomSuggestedQuestions } from '../../utils/questions'

import styles from './Chat.module.css'

const enum messageStatus {
  NotRunning = 'Not Running',
  Processing = 'Processing',
  Done = 'Done',
}

const Chat = () => {
  const appStateContext = useContext(AppStateContext)
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null)
  const chatMessageStreamRef = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false)
  const [activeCitation, setActiveCitation] = useState<Citation>()
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false)
  const [isIntentsPanelOpen, setIsIntentsPanelOpen] = useState<boolean>(false)
  const abortFuncs = useRef([] as AbortController[])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const messagesRef = useRef<ChatMessage[]>([])
  const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning)
  const messageQueueRef = useRef<
    Array<{
      event: string
      content: string
      message_id: string
      conversation?: Conversation
      sections: { [key: string]: string[] }
      error?: ChatErrorResponse
    }>
  >([])
  const isProcessingRef = useRef(false)
  const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true)
  const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>()
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false)
  const [isTitleMaxLengthReached, setIsTitleMaxLengthReached] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openTitleMenu = Boolean(anchorEl)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const isExperimentDeletedAlertOpen = appStateContext?.state.currentChat?.extended_data?.experiment_deleted
  const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(false)
  const [anchorInfoBalloonEl, setAnchorInfoBalloonEl] = useState<null | HTMLElement>(null)
  const openInfoBalloonMenu = Boolean(anchorInfoBalloonEl)
  const chatInputContainerRef = useRef<HTMLDivElement | null>(null)
  const [chatInputHeight, setChatInputHeight] = useState<number>(0)
  const { blocked: isCreateExperimentBlocked, reason: createExperimentBlockedReason } = getCreateExperimentBlock(
    appStateContext?.state.user,
  )
  const createExperimentBlockedMessage = getCreateExperimentBlockedMessage(createExperimentBlockedReason)
  const [createTooltipOpen, setCreateTooltipOpen] = useState(false)

  const ERROR = 'error'
  const allQuestions = useMemo(() => {
    const agents = Object.values(AgentNames).filter(
      agent =>
        agent !== AgentNames.greetings &&
        (!isCreateExperimentBlocked ||
          (isCreateExperimentBlocked && agent !== AgentNames.create_experiment_form_filler)),
    )
    return getRandomSuggestedQuestions(agents)
  }, [appStateContext?.state.currentChat?.id])

  const processNextMessage = useCallback(() => {
    if (isProcessingRef.current || messageQueueRef.current.length === 0) {
      return
    }

    isProcessingRef.current = true
    const message = messageQueueRef.current.shift()!

    const { event, content, message_id, conversation, sections, error } = message

    if (event === 'start') {
      setShowLoadingMessage(false)
      setMessages(prevMessages => [
        ...prevMessages,
        {
          id: message_id,
          role: 'assistant',
          content: '',
          date: new Date().toISOString(),
          agent_name: 'assistant',
        },
      ])
    } else if (event === 'chunk') {
      setMessages(prevMessages => {
        if (prevMessages.length === 0) {
          console.warn('Received chunk but no messages exist')
          return prevMessages
        }
        const lastMessage = prevMessages[prevMessages.length - 1]
        if (lastMessage.role !== 'assistant') {
          console.warn('Last message is not an assistant message')
          return prevMessages
        }
        const newContent = lastMessage.content + content
        const newMessage = { ...lastMessage, content: newContent }
        return [...prevMessages.slice(0, -1), newMessage]
      })
    } else if (event === 'complete') {
      setIsLoading(false)

      if (conversation) {
        // Use messagesRef to get current messages state outside of setMessages
        const currentMessages = messagesRef.current
        const newMessages = [
          ...filter(currentMessages, message => message.id !== message_id),
          {
            id: message_id,
            role: 'assistant',
            content: content,
            date: new Date().toISOString(),
            agent_name: 'assistant',
            sections: sections ?? {},
          },
        ]

        // Update local state
        setMessages(newMessages)

        // Dispatch context updates outside of setMessages
        appStateContext?.dispatch({
          type: 'UPDATE_CURRENT_CHAT',
          payload: { ...conversation, messages: newMessages },
        })
        appStateContext?.dispatch({
          type: 'UPDATE_CHAT_HISTORY',
          payload: { ...conversation, messages: newMessages },
        })
        if (conversation?.extended_data?.keep_validating)
          appStateContext?.dispatch({ type: 'SET_SHOULD_VALIDATE_FORM', payload: true })
        if (conversation?.extended_data?.user_requested_new_conversation) {
          appStateContext?.dispatch({ type: 'SET_IS_NEW_CHAT_DIALOG_OPEN', payload: true })
        }
      }
    } else if (event === 'error') {
      setIsLoading(false)
      setShowLoadingMessage(false)
      const currentMessages = messagesRef.current
      const newMessages = [
        ...filter(currentMessages, message => message.id !== message_id),
        {
          id: message_id,
          role: ERROR,
          content: error?.message,
          date: new Date().toISOString(),
          agent_name: 'error',
          message_type: 'error',
        },
      ]

      setMessages(newMessages as ChatMessage[])

      appStateContext?.dispatch({
        type: 'UPDATE_CURRENT_CHAT',
        payload: { ...(conversation as Conversation), messages: newMessages as ChatMessage[] },
      })
      appStateContext?.dispatch({
        type: 'UPDATE_CHAT_HISTORY',
        payload: { ...(conversation as Conversation), messages: newMessages as ChatMessage[] },
      })
    } else if (event === 'cancelled') {
      const currentMessages = messagesRef.current
      const newMessages = [...filter(currentMessages, message => message.id !== message_id)]
      setMessages(newMessages)
      setIsLoading(false)
      setShowLoadingMessage(false)
    }

    setTimeout(() => {
      isProcessingRef.current = false
      processNextMessage()
    }, 0)
  }, [])

  const processStreamMessage = useCallback(
    ({
      event,
      content,
      message_id,
      conversation,
      sections,
      error,
    }: {
      event: string
      content: string
      message_id: string
      conversation?: Conversation
      sections: { [key: string]: string[] }
      error?: ChatErrorResponse
    }) => {
      // Add to queue
      messageQueueRef.current.push({ event, content, message_id, conversation, sections, error })

      // Process if not already processing
      if (!isProcessingRef.current) {
        processNextMessage()
      }
    },
    [processNextMessage],
  )

  // Use WebSocket for additional message handling (shares the same connection as notifications)
  const handleWebSocketMessage = useCallback(
    (type: string, data: any) => {
      // Add your custom message handling logic here
      if (type === 'stream') {
        // Handle your custom message type
        processStreamMessage(data)
      }
    },
    [processStreamMessage],
  )

  const { isConnected: isWebSocketConnected, sendMessage: sendWebSocketMessage } = useWebSocket({
    onMessage: handleWebSocketMessage,
  })

  const sendUserMessage = (
    question: string | [{ type: string; text: string }, { type: string; image_url: { url: string } }],
    id?: string,
  ) => {
    setIsLoading(true)
    setShowLoadingMessage(true)
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: question,
      date: new Date().toISOString(),
      agent_name: 'user_proxy',
    }
    const formattedDate = new Date().toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })

    setMessages([...messages, userMessage])

    sendWebSocketMessage({
      conversation_id: id,
      content: question,
      should_validate_form: appStateContext?.state.shouldValidateForm ?? false,
      title: appStateContext?.state.currentChat?.title ?? formattedDate,
    })
  }

  const checkScrollPosition = useCallback(() => {
    if (!chatMessageStreamEnd.current) return

    const rect = chatMessageStreamEnd.current.getBoundingClientRect()
    const isAtBottom = rect.bottom <= window.innerHeight + 100
    setIsScrollButtonVisible(!isAtBottom)
  }, [])

  useEffect(() => {
    if (messages.length === 0) {
      setIsScrollButtonVisible(false)
      return
    }

    checkScrollPosition()

    if (chatMessageStreamRef.current) {
      chatMessageStreamRef.current.addEventListener('scroll', checkScrollPosition)
      window.addEventListener('resize', checkScrollPosition)

      return () => {
        if (chatMessageStreamRef.current) {
          chatMessageStreamRef.current.removeEventListener('scroll', checkScrollPosition)
        }
        window.removeEventListener('resize', checkScrollPosition)
      }
    }
  }, [messages.length, checkScrollPosition])

  useEffect(() => {
    if (!chatInputContainerRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setChatInputHeight(entry.contentRect.height)
      }
    })

    resizeObserver.observe(chatInputContainerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const handleTitleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleInfoBalloonClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorInfoBalloonEl(event.currentTarget)
  }

  const errorDialogContentProps = {
    type: DialogType.close,
    title: errorMsg?.title,
    closeButtonAriaLabel: 'Close',
    subText: errorMsg?.subtitle,
  }

  const modalProps = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { maxWidth: 450 } },
  }

  useEffect(() => {
    if (
      appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.Working &&
      appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured &&
      appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail &&
      hideErrorDialog
    ) {
      const subtitle = `Please reach out to support for assistance.`
      setErrorMsg({
        title: 'Unexpected error occurred',
        subtitle: subtitle,
      })
      toggleErrorDialog()
    }
  }, [appStateContext?.state.isCosmosDBAvailable])

  const handleErrorDialogClose = () => {
    toggleErrorDialog()
    setTimeout(() => {
      setErrorMsg(null)
    }, 500)
  }

  useEffect(() => {
    setIsLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
  }, [appStateContext?.state.chatHistoryLoadingState])

  const stopGenerating = () => {
    sendWebSocketMessage({ action: 'stop' })
  }

  // Keep messagesRef in sync with messages state
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  useEffect(() => {
    if (appStateContext?.state.currentChat) {
      setMessages(appStateContext.state.currentChat.messages)
    } else {
      setProcessMessages(messageStatus.Processing)
      setMessages([])
      setIsCitationPanelOpen(false)
      setIsIntentsPanelOpen(false)
      setActiveCitation(undefined)
      setProcessMessages(messageStatus.Done)
    }
  }, [appStateContext?.state.currentChat])

  const onQuestionClicked = (question: string) => {
    sendUserMessage(question, appStateContext?.state.currentChat?.id)
  }

  const handleCreateExperiment = async () => {
    if (isCreateExperimentBlocked) return

    const title = new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    try {
      const conversation = await createExperimentForm(title)
      if (conversation) {
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: conversation })
      }
    } catch (err) {
      console.error('Error creating experiment', err)
    }
  }

  useEffect(() => {
    if (appStateContext?.state.homeChatMessage) {
      sendUserMessage(appStateContext.state.homeChatMessage)
      appStateContext?.dispatch({ type: 'SET_HOME_CHAT_MESSAGE', payload: null })
    }
  }, [])

  useLayoutEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
  }, [showLoadingMessage, processMessages])

  const onShowCitation = (citation: Citation) => {
    setActiveCitation(citation)
    setIsCitationPanelOpen(true)
  }

  const onShowExecResult = (answerId: string) => {
    setIsIntentsPanelOpen(true)
  }

  const onViewSource = (citation: Citation) => {
    if (citation.url && !citation.url.includes('blob.core')) {
      window.open(citation.url, '_blank')
    }
  }

  const parseCitationFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === 'tool' && typeof message?.content === 'string') {
      try {
        const toolMessage = JSON.parse(message.content) as ToolMessageContent
        return toolMessage.citations
      } catch {
        return []
      }
    }
    return []
  }

  const parsePlotFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === 'tool' && typeof message?.content === 'string') {
      try {
        const execResults = JSON.parse(message.content) as AzureSqlServerExecResults
        const codeExecResult = execResults.all_exec_results.at(-1)?.code_exec_result

        if (codeExecResult === undefined) {
          return null
        }
        return codeExecResult.toString()
      } catch {
        return null
      }
      // const execResults = JSON.parse(message.content) as AzureSqlServerExecResults;
      // return execResults.all_exec_results.at(-1)?.code_exec_result;
    }
    return null
  }

  const handleSaveEdit = async (e: any) => {
    e.preventDefault()
    const trimmedValue = trim(e.target.value, ' ')
    if (
      isEmpty(trimmedValue) ||
      trimmedValue === appStateContext?.state.currentChat?.title ||
      isTitleMaxLengthReached
    ) {
      setIsTitleMaxLengthReached(false)
      setIsEditingTitle(false)
      return
    }

    const response = await historyRename(appStateContext?.state.currentChat?.id ?? '', trimmedValue)
    if (!response.ok) {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Rename failed. Try again later.', severity: 'warning' },
      })
    } else {
      setIsEditingTitle(false)
      appStateContext?.dispatch({
        type: 'UPDATE_CHAT_TITLE',
        payload: { ...appStateContext?.state.currentChat, title: trimmedValue } as Conversation,
      })
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Conversation renamed successfully.', severity: 'success' },
      })
    }
  }

  const handleDeleteConversation = async () => {
    const conversationId = appStateContext?.state.currentChat?.id
    if (!conversationId) {
      return
    }
    setIsDeleteModalOpen(false)
    historyDelete(conversationId)
      .then(res => {
        if (res.ok) {
          appStateContext?.dispatch({ type: 'DELETE_CHAT_ENTRY', payload: conversationId })
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Conversation deleted successfully.', severity: 'success' },
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Delete failed. Try again later.', severity: 'warning' },
          })
        }
      })
      .catch(err => {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Delete failed. Try again later.', severity: 'warning' },
        })
      })
  }

  const experimentForm = (
    <Stack
      horizontal
      className={styles.formContainer}>
      <ExperimentForm collapseForm={() => handleExpandCollapseExperimentForm(false)} />
    </Stack>
  )

  const handleExpandCollapseExperimentForm = (expand: boolean) => {
    expandCollapseExperimentForm(appStateContext?.state.currentChat?.id ?? '', expand)
      .then(response => {
        if (response) {
          const newConversation = response as Conversation

          newConversation.messages = messages

          appStateContext?.dispatch({
            type: 'UPDATE_CURRENT_CHAT',
            payload: newConversation,
          })
        }
      })
      .catch(err => {
        console.error(`Error fetching chat history for conversation ${appStateContext?.state.currentChat?.id}`, err)
      })
  }

  return (
    <div
      className={styles.container}
      role="main">
      <Stack className={styles.chatRoot}>
        <div className={styles.chatContainer}>
          {!appStateContext?.state.currentChat?.extended_data?.experiment_form_expanded && (
            <CustomTooltip
              title="Expand experiment form"
              placement="top">
              <MuiIconButton
                className={styles.sideToggleButton}
                onClick={() => {
                  handleExpandCollapseExperimentForm(true)
                }}
                aria-label="Toggle experiment form panel">
                <img src={CollapseButton} />
              </MuiIconButton>
            </CustomTooltip>
          )}
          {!isEmpty(appStateContext?.state.currentChat?.title) && (
            <div className={styles.titleContainer}>
              <div className={styles.chatTitleContainer}>
                {isEditingTitle ? (
                  <CustomTooltip
                    title="You've reached the maximum character limit"
                    open={isTitleMaxLengthReached}
                    classes={{ tooltip: styles.chatTitleInputTooltip }}>
                    <Input
                      disableUnderline
                      autoFocus
                      className={styles.chatTitleInput}
                      classes={{
                        input: isTitleMaxLengthReached ? styles.chatTitleInputTextMaxLength : styles.chatTitleInputText,
                        root: styles.chatTitleInputRoot,
                      }}
                      onChange={e => {
                        if (e.target.value.length > 100) {
                          setIsTitleMaxLengthReached(true)
                        } else {
                          setIsTitleMaxLengthReached(false)
                        }
                      }}
                      defaultValue={appStateContext?.state.currentChat?.title}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          handleSaveEdit(e)
                        }
                      }}
                      onBlur={handleSaveEdit}
                    />
                  </CustomTooltip>
                ) : (
                  <>
                    <div
                      onClick={handleTitleClick}
                      className={styles.chatTitleMenuContainer}>
                      <p className={styles.chatTitle}>{appStateContext?.state.currentChat?.title}</p>
                      <ExpandMore />
                    </div>
                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={openTitleMenu}
                      onClose={() => setAnchorEl(null)}
                      slotProps={{
                        paper: {
                          className: styles.menuContainer,
                        },
                        list: {
                          'aria-labelledby': 'basic-button',
                        },
                      }}>
                      <MenuItem
                        onClick={() => {
                          setIsEditingTitle(true)
                          setAnchorEl(null)
                        }}>
                        <ListItemIcon classes={{ root: styles.iconContainer }}>
                          <img src={PenIcon} />
                        </ListItemIcon>
                        <p className={styles.menuItemText}>Rename</p>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setIsDeleteModalOpen(true)
                          setAnchorEl(null)
                        }}>
                        <ListItemIcon classes={{ root: styles.iconContainer }}>
                          <img
                            src={DeleteIcon}
                            className={styles.deleteIcon}
                          />
                        </ListItemIcon>
                        <p className={styles.menuItemText}>Delete conversation</p>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </div>
              <IconButton
                onClick={handleInfoBalloonClick}
                aria-label="Info balloon"
                className={styles.infoBalloonButton}>
                <img src={InfoBalloon} />
              </IconButton>
              <Menu
                id="info-balloon-menu"
                anchorEl={anchorInfoBalloonEl}
                open={openInfoBalloonMenu}
                onClose={() => setAnchorInfoBalloonEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                slotProps={{
                  paper: {
                    className: styles.menuContainer,
                  },
                  list: {
                    'aria-labelledby': 'basic-button',
                  },
                }}>
                <div className={styles.infoBalloonMenuContainer}>
                  <div className={styles.infoBalloonMenuTitle}>
                    <div className={styles.infoBalloonMenuTitleTextContainer}>
                      <HelpOutlineOutlined className={styles.infoBalloonMenuTitleTextIcon} />
                      <p className={styles.infoBalloonMenuTitleText}>What can you do here?</p>
                    </div>
                    <IconButton
                      aria-label="close"
                      onClick={() => setAnchorInfoBalloonEl(null)}
                      className={styles.infoBalloonMenuTitleTextIconCloseButton}>
                      <Close className={styles.infoBalloonMenuTitleTextIconClose} />
                    </IconButton>
                  </div>
                  <ul className={styles.infoBalloonMenuListContainer}>
                    <li className={styles.infoBalloonMenuList}>
                      <Check className={styles.infoBalloonMenuListCheckIcon} />
                      Be guided through the process
                    </li>
                    <li className={styles.infoBalloonMenuList}>
                      <Check className={styles.infoBalloonMenuListCheckIcon} />
                      Have the necessary guidance
                    </li>
                    <li className={styles.infoBalloonMenuList}>
                      <Check className={styles.infoBalloonMenuListCheckIcon} />
                      Select the most suitable approach
                    </li>
                    <li className={styles.infoBalloonMenuList}>
                      <Check className={styles.infoBalloonMenuListCheckIcon} />
                      Personalized recommendations
                    </li>
                  </ul>
                </div>
              </Menu>
            </div>
          )}
          {isExperimentDeletedAlertOpen && (
            <Alert
              className={styles.alert}
              severity="warning"
              icon={false}>
              <span className={styles.alertText}>
                The linked experiment has been deleted. To start a new experiment, please create a{' '}
                <span
                  className={styles.alertLink}
                  onClick={() => appStateContext?.dispatch({ type: 'SET_IS_NEW_CHAT_DIALOG_OPEN', payload: true })}>
                  new conversation
                </span>
              </span>
            </Alert>
          )}
          {appStateContext?.state.currentChat?.extended_data?.view_only && (
            <Alert
              className={styles.alert}
              severity="warning"
              icon={false}>
              <span className={styles.alertText}>
                You're currently in <strong>view-only mode</strong>. Edits to this experiment can't be made through the
                chat.
              </span>
            </Alert>
          )}
          {!messages || messages.length < 1 ? (
            <Stack className={styles.chatEmptyState}>
              <div className={styles.chatEmptyStateTitleContainer}>
                <h1
                  className={
                    appStateContext?.state.currentChat?.extended_data?.experiment_form_expanded
                      ? styles.chatEmptyStateTitleExpanded
                      : styles.chatEmptyStateTitle
                  }>
                  Hello,{' '}
                  <span className={styles.chatEmptyStateTitleName}>
                    {first(words(appStateContext?.state.user?.name))}
                  </span>
                </h1>
                <img
                  src={SparkleGradient}
                  className={styles.chatIcon}
                  aria-hidden="true"
                />
              </div>
              <h2 className={styles.chatEmptyStateSubtitle}>
                Welcome to <span>Experimentation @ Scale</span> platform.
              </h2>
              <h2 className={styles.chatEmptyStateSubtitle2}>
                I can help you choose the right methodologies and KPIs,{' '}
                <CustomTooltip
                  open={isCreateExperimentBlocked ? createTooltipOpen : false}
                  onOpen={() => isCreateExperimentBlocked && setCreateTooltipOpen(true)}
                  onClose={() => setCreateTooltipOpen(false)}
                  title={
                    createExperimentBlockedMessage.showRequestAccess ? (
                      <>
                        {createExperimentBlockedMessage.message} Submit a new
                        <a
                          onClick={() => {
                            setCreateTooltipOpen(false)
                            window.dispatchEvent(new CustomEvent('openAccessRequestDialog'))
                          }}
                          role="button"
                          className={styles.createNewExperimentLink}>
                          access request.
                        </a>
                      </>
                    ) : (
                      createExperimentBlockedMessage.message
                    )
                  }
                  disableFocusListener={!isCreateExperimentBlocked}
                  disableHoverListener={!isCreateExperimentBlocked}
                  disableInteractive={!isCreateExperimentBlocked}
                  disableTouchListener={!isCreateExperimentBlocked}>
                  <span
                    className={isCreateExperimentBlocked ? styles.underlined : styles.createNewExperimentLink}
                    onClick={handleCreateExperiment}>
                    create new experiment
                  </span>
                </CustomTooltip>
                , and surface insights through best practices decoded and data queries. What would you like to do?
              </h2>
              <CardsCarousel
                onQuestionClick={onQuestionClicked}
                allQuestions={allQuestions}
              />
            </Stack>
          ) : (
            <div
              ref={chatMessageStreamRef}
              className={styles.chatMessageStream}
              style={{ marginBottom: isLoading ? '40px' : '0px' }}
              role="log">
              {messages.map((answer, index) => (
                <div key={index}>
                  {answer.role === 'user' && answer.content.length > 0 ? (
                    <div
                      className={styles.chatMessageUser}
                      tabIndex={0}>
                      <div className={styles.chatMessageUserMessage}>
                        {typeof answer.content === 'string' && answer.content ? (
                          answer.content
                        ) : Array.isArray(answer.content) ? (
                          <>
                            {answer.content[0].text}{' '}
                            <img
                              className={styles.uploadedImageChat}
                              src={answer.content[1].image_url.url}
                              alt="Uploaded Preview"
                            />
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : answer.role === 'assistant' || answer.role === ERROR ? (
                    <div className={styles.chatMessageGpt}>
                      {typeof answer.content === 'string' && (
                        <>
                          <div className={styles.assistantIconContainer}>
                            <img
                              src={AssistantIcon}
                              alt="Assistant Icon"
                            />
                          </div>
                          {answer.content.length === 0 ? (
                            <div className={styles.thinkingContainer}>
                              <ThinkingAnimation />
                            </div>
                          ) : (
                            <Answer
                              answer={{
                                answer: answer.content,
                                citations: parseCitationFromMessage(messages[index - 1]),
                                generated_chart: parsePlotFromMessage(messages[index - 1]),
                                message_id: answer.id,
                                feedback: answer.feedback,
                                agent_name: answer.agent_name,
                                sections: answer.sections,
                                error: answer.message_type === 'error' ? answer.content : undefined,
                              }}
                              onCitationClicked={c => onShowCitation(c)}
                              onQuestionClicked={onQuestionClicked}
                            />
                          )}
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
              {showLoadingMessage && (
                <div key="loading">
                  <div className={styles.jumpingDotsBalloon}>
                    <JumpingDots />
                    <div className={styles.jumpingDotsBalloonText}>Generating response...</div>
                  </div>
                </div>
              )}
              <div ref={chatMessageStreamEnd} />
            </div>
          )}
          {isScrollButtonVisible && (
            <div
              className={styles.fastDownArrowContainer}
              style={{ bottom: `${chatInputHeight + 50}px` }}>
              <IconButton
                className={styles.fastDownArrow}
                onClick={() => {
                  chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
                }}>
                <ArrowDownward />
              </IconButton>
            </div>
          )}
          <div
            className={styles.chatInputContainer}
            ref={chatInputContainerRef}>
            {isLoading && messages.length > 0 && (
              <Stack
                className={styles.stopGeneratingContainer}
                role="button"
                aria-label="Stop generating"
                tabIndex={0}
                onClick={stopGenerating}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? stopGenerating() : null)}>
                <SquareRegular
                  className={styles.stopGeneratingIcon}
                  aria-hidden="true"
                />
                <span
                  className={styles.stopGeneratingText}
                  aria-hidden="true">
                  Stop generating
                </span>
              </Stack>
            )}
            <Stack className={styles.chatInputInnerContainer}>
              <QuestionInput
                clearOnSend
                placeholder="Type a new question..."
                disabled={isLoading}
                onSend={sendUserMessage}
                conversationId={
                  appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined
                }
              />
            </Stack>
            <p className={styles.chatDisclaimer}>
              This experience uses Generative AI. Always verify critical information.
            </p>
          </div>
          <Dialog
            hidden={hideErrorDialog}
            onDismiss={handleErrorDialogClose}
            dialogContentProps={errorDialogContentProps}
            modalProps={modalProps}></Dialog>
        </div>
        {/* Citation Panel */}
        {messages && messages.length > 0 && isCitationPanelOpen && activeCitation && (
          <Stack.Item
            className={styles.citationPanel}
            tabIndex={0}
            role="tabpanel"
            aria-label="Citations Panel">
            <Stack
              aria-label="Citations Panel Header Container"
              horizontal
              className={styles.citationPanelHeaderContainer}
              horizontalAlign="space-between"
              verticalAlign="center">
              <span
                aria-label="Citations"
                className={styles.citationPanelHeader}>
                Citations
              </span>
              <IconButton
                iconProps={{ iconName: 'Cancel' }}
                aria-label="Close citations panel"
                onClick={() => setIsCitationPanelOpen(false)}
              />
            </Stack>
            <h5
              className={styles.citationPanelTitle}
              tabIndex={0}
              title={
                activeCitation.url && !activeCitation.url.includes('blob.core')
                  ? activeCitation.url
                  : activeCitation.title ?? ''
              }
              onClick={() => onViewSource(activeCitation)}>
              {activeCitation.title}
            </h5>
            <div tabIndex={0}>
              <ReactMarkdown
                linkTarget="_blank"
                className={styles.citationPanelContent}
                children={DOMPurify.sanitize(activeCitation.content, { ALLOWED_TAGS: XSSAllowTags })}
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
              />
            </div>
          </Stack.Item>
        )}
        {messages && messages.length > 0 && isIntentsPanelOpen && (
          <Stack.Item
            className={styles.citationPanel}
            tabIndex={0}
            role="tabpanel"
            aria-label="Intents Panel">
            <Stack
              aria-label="Intents Panel Header Container"
              horizontal
              className={styles.citationPanelHeaderContainer}
              horizontalAlign="space-between"
              verticalAlign="center">
              <span
                aria-label="Intents"
                className={styles.citationPanelHeader}>
                Intents
              </span>
              <IconButton
                iconProps={{ iconName: 'Cancel' }}
                aria-label="Close intents panel"
                onClick={() => setIsIntentsPanelOpen(false)}
              />
            </Stack>
          </Stack.Item>
        )}
      </Stack>
      {
        <Collapse
          className={
            appStateContext?.state.currentChat?.extended_data?.experiment_form_expanded
              ? styles.formRoot
              : styles.formRootClosed
          }
          in={appStateContext?.state.currentChat?.extended_data?.experiment_form_expanded}
          orientation="horizontal"
          classes={{
            wrapperInner: styles.formRootInner,
          }}>
          {experimentForm}
        </Collapse>
      }
      <CustomDialog
        open={isDeleteModalOpen}
        setClose={() => setIsDeleteModalOpen(false)}
        title="Delete conversation"
        primaryAction={() => handleDeleteConversation()}
        primaryActionButtonText="Delete"
        secondaryAction={() => setIsDeleteModalOpen(false)}
        secondaryActionButtonText="Cancel">
        <Stack>
          <Typography className={styles.deleteDialogContentText}>
            This action cannot be undone. If there's an active experiment associated to this chat, it will not be
            deleted.
          </Typography>
        </Stack>
      </CustomDialog>
    </div>
  )
}

export default Chat
